import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { useTranslations } from '../../context/TranslationsContext';
import {
  CogIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import QRCodeDisplay from './QRCodeDisplay';

const SettingsManagement = () => {
  const { state } = useApp();
  const { translations } = useTranslations();
  const t = translations[state.currentLanguage];
  const [settings, setSettings] = useState({
    pricing: null,
    general: null
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('whatsapp');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    if (!state.token) return;
    
    setLoading(true);
    try {
      const response = await fetch('/api/settings/', {
        headers: {
          'Authorization': `Bearer ${state.token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePricingUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const formData = new FormData(e.target);
      const pricingData = {
        price_per_sqm: parseFloat(formData.get('price_per_sqm')),
        price_per_kg: parseFloat(formData.get('price_per_kg')),
        currency: formData.get('currency')
      };

      const response = await fetch('/api/settings/pricing', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${state.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(pricingData)
      });

      if (response.ok) {
        setMessage('تم تحديث إعدادات الأسعار بنجاح');
        fetchSettings();
      } else {
        setMessage('فشل في تحديث إعدادات الأسعار');
      }
    } catch (error) {
      console.error('Error updating pricing settings:', error);
      setMessage('خطأ في تحديث إعدادات الأسعار');
    } finally {
      setSaving(false);
    }
  };

  const handleGeneralUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const formData = new FormData(e.target);
      const generalData = {
        app_name: formData.get('app_name'),
        app_name_arabic: formData.get('app_name_arabic'),
        company_name: formData.get('company_name'),
        company_name_arabic: formData.get('company_name_arabic'),
        contact_phone: formData.get('contact_phone'),
        contact_email: formData.get('contact_email'),
        address: formData.get('address'),
        address_arabic: formData.get('address_arabic')
      };

      const response = await fetch('/api/settings/general', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${state.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(generalData)
      });

      if (response.ok) {
        setMessage('تم تحديث الإعدادات العامة بنجاح');
        fetchSettings();
      } else {
        setMessage('فشل في تحديث الإعدادات العامة');
      }
    } catch (error) {
      console.error('Error updating general settings:', error);
      setMessage('خطأ في تحديث الإعدادات العامة');
    } finally {
      setSaving(false);
    }
  };

  const renderPricingSettings = () => (
    <div className="bg-white shadow-lg rounded-xl p-8">
      <div className="flex items-center mb-6">
        <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg mr-4">
          <CurrencyDollarIcon className="h-8 w-8 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t.priceSettings}</h2>
          <p className="text-gray-600">{t.manageStoragePricing}</p>
        </div>
      </div>

      <form onSubmit={handlePricingUpdate} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t.pricePerSqm} <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="price_per_sqm"
              defaultValue={settings.pricing?.price_per_sqm || 0.25}
              step="0.01"
              min="0"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t.pricePerKg} <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="price_per_kg"
              defaultValue={settings.pricing?.price_per_kg || 0.1}
              step="0.01"
              min="0"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t.currency}
            </label>
            <select
              name="currency"
              defaultValue={settings.pricing?.currency || 'SAR'}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200"
            >
              <option value="SAR">ريال سعودي (SAR)</option>
              <option value="USD">دولار أمريكي (USD)</option>
              <option value="EUR">يورو (EUR)</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 text-base font-medium text-white bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? t.saving : t.saveSettings}
          </button>
        </div>
      </form>
    </div>
  );

  const renderWhatsAppSettings = () => (
    <div className="space-y-6">
      <div className="bg-white shadow-lg rounded-xl p-8">
        <div className="flex items-center mb-6">
          <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg mr-4">
            <span className="text-2xl">📱</span>
          </div>
          <div>
          <h2 className="text-2xl font-bold text-gray-900">{t.whatsappSettings}</h2>
          <p className="text-gray-600">{t.connectWhatsapp}</p>
          </div>
        </div>

        <QRCodeDisplay />
      </div>
    </div>
  );

  const renderGeneralSettings = () => (
    <div className="bg-white shadow-lg rounded-xl p-8">
      <div className="flex items-center mb-6">
        <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg mr-4 ml-2">
          <BuildingOfficeIcon className="h-8 w-8 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t.generalSettings}</h2>
          <p className="text-gray-600">{t.companyAppInfo}</p>
        </div>
      </div>

      <form onSubmit={handleGeneralUpdate} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t.appNameEnglish}
            </label>
            <input
              type="text"
              name="app_name"
              defaultValue={settings.general?.app_name || 'خزنلي'}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t.appNameArabic}
            </label>
            <input
              type="text"
              name="app_name_arabic"
              defaultValue={settings.general?.app_name_arabic || 'خزنلي'}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t.companyNameEnglish}
            </label>
            <input
              type="text"
              name="company_name"
              defaultValue={settings.general?.company_name || 'خزنلي'}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t.companyNameArabic}
            </label>
            <input
              type="text"
              name="company_name_arabic"
              defaultValue={settings.general?.company_name_arabic || 'خزنلي'}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t.contactPhone}
            </label>
            <input
              type="tel"
              name="contact_phone"
              defaultValue={settings.general?.contact_phone || ''}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t.contactEmail}
            </label>
            <input
              type="email"
              name="contact_email"
              defaultValue={settings.general?.contact_email || ''}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t.addressEnglish}
            </label>
            <textarea
              name="address"
              defaultValue={settings.general?.address || ''}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t.addressArabic}
            </label>
            <textarea
              name="address_arabic"
              defaultValue={settings.general?.address_arabic || ''}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 text-base font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
          </button>
        </div>
      </form>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white shadow-lg rounded-xl p-8">
            <div className="flex justify-center items-center py-16">
              <div className="flex flex-col items-center space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 border-t-indigo-600"></div>
                <p className="text-gray-600 font-medium">{t.loadingSettings}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white shadow-lg rounded-xl p-8 border border-gray-100">
          <div className="flex items-center space-x-4 rtl:space-x-reverse">
            <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg ml-2">
              <CogIcon className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{t.settings}</h1>
              <p className="text-lg text-gray-600">{t.manageAppSettings}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white shadow-lg rounded-xl border border-gray-100">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-8 rtl:space-x-reverse">
              <button
                onClick={() => setActiveTab('whatsapp')}
                className={`py-6 px-4 border-b-3 font-semibold text-base transition-all duration-200 ${
                  activeTab === 'whatsapp'
                    ? 'border-green-500 text-green-600 bg-green-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <span className="text-lg">📱</span>
                  <span>{t.whatsappSettings}</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('pricing')}
                className={`py-6 px-4 border-b-3 font-semibold text-base transition-all duration-200 ${
                  activeTab === 'pricing'
                    ? 'border-green-500 text-green-600 bg-green-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <CurrencyDollarIcon className="h-5 w-5" />
                  <span>{t.priceSettings}</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('general')}
                className={`py-6 px-4 border-b-3 font-semibold text-base transition-all duration-200 ${
                  activeTab === 'general'
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <BuildingOfficeIcon className="h-5 w-5" />
                  <span>{t.generalSettings}</span>
                </div>
              </button>
            </nav>
          </div>

          <div className="p-8">
            {message && (
              <div className={`mb-6 p-4 rounded-xl flex items-center space-x-3 rtl:space-x-reverse ${
                message.includes('نجح') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                <CheckCircleIcon className="h-5 w-5" />
                <span>{message}</span>
              </div>
            )}

            {activeTab === 'whatsapp' ? renderWhatsAppSettings() : 
             activeTab === 'pricing' ? renderPricingSettings() : 
             renderGeneralSettings()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsManagement;
